'use client' // Error boundaries must be Client Components
 
import { useEffect, useState } from 'react'
import { IconArchieAvatar } from '@/components/ui/icons'

interface ErrorLogRequest {
  error: {
    message: string
    stack?: string
    digest?: string
    name?: string
  }
  errorCode?: string
  userAgent?: string
  url?: string
  timestamp?: string
}

const logErrorToServer = async (error: Error & { digest?: string }, errorCode: string = 'gen') => {
  try {
    const errorData: ErrorLogRequest = {
      error: {
        message: error.message,
        stack: error.stack,
        digest: error.digest,
        name: error.name
      },
      errorCode,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString()
    }

    const response = await fetch('/api/errors', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(errorData)
    })

    if (!response.ok) {
      console.error('Failed to log error to server:', response.statusText)
    }
  } catch (serverError) {
    console.error('Error logging to server:', serverError)
  }
}

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [isLoggingError, setIsLoggingError] = useState(false)

  useEffect(() => {
    // Set page title
    document.title = 'Error - Smokeball Archie'
    
    // Log the error to console (existing behavior)
    console.error(error)
    
    // Also log to server
    const logError = async () => {
      setIsLoggingError(true)
      try {
        await logErrorToServer(error, 'gen') // Using 'gen' for general errors as requested
      } finally {
        setIsLoggingError(false)
      }
    }
    
    logError()
  }, [error])

  const handleTryAgain = () => {
    try {
      reset()
    } catch (resetError) {
      console.error('Reset failed, reloading page:', resetError)
      window.location.reload()
    }
  }

  const handleReload = () => {
    window.location.reload()
  }
 
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
        <div className="mb-4">
          <div className="mx-auto flex items-center justify-center h-12 w-12">
            <IconArchieAvatar className="h-12 w-12" />
          </div>
        </div>
        
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Oops! Something went wrong</h2>
        <p className="text-sm text-gray-600 mb-6">
          I&apos;m sorry, but I&apos;ve encountered an unexpected error. Don&apos;t worry - my team has been notified and we&apos;re working to fix this.
        </p>
        
        {error.digest && (
          <p className="text-xs text-gray-500 mb-4 font-mono bg-gray-100 p-2 rounded">
            Error ID: {error.digest}
          </p>
        )}
        
        <div className="space-y-4">
          <button
            onClick={handleTryAgain}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
            disabled={isLoggingError}
          >
            {isLoggingError ? 'Reporting error...' : 'Try Again'}
          </button>
          
          <div className="text-center">
            <span className="text-sm text-gray-600">or </span>
            <button
              onClick={handleReload}
              className="text-sm text-blue-600 hover:text-blue-700 hover:underline font-medium transition-colors duration-200"
            >
              Reload Archie
            </button>
          </div>
        </div>
        
        <p className="text-xs text-gray-500 mt-4">
          If this keeps happening, please reach out to support - I&apos;d love to help you get back on track!
        </p>
      </div>
    </div>
  )
}